import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

import { getAgentModelName } from '../lib/agentModels';
import { ASSIGNABLE_LABELS, PROMPT_LABEL_META, isPromptLabel } from '../lib/promptLabels';
import type { AgentModelOptionsResponse, PromptLabel } from '../services/projects.api';
import ModelChangeChoice from './ModelChangeChoice';

const DEFAULT_LABEL: PromptLabel = 'latest';

/** Our API returns the error text as a string body (Koa `ctx.throw`). */
const extractErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (typeof e === 'object' && e !== null && 'data' in e) {
    const { data } = e as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return 'No se pudo cambiar la etiqueta.';
};

export interface ChangeLabelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** e.g. "v6" — shown in subtitle */
  versionLabel: string;
  currentLabel: string | null;
  isSaving?: boolean;
  /** Model saved on this version (`provider/model`). */
  versionModel: string;
  /** Undefined while the model list loads or when it fails: the promotion then keeps the production model. */
  modelOptions: AgentModelOptionsResponse | undefined;
  /** Master versions with subprojects: applying the model also moves every subproject. */
  movesSubprojects?: boolean;
  /** `applyModel` is only true for production, when CS chooses the version's model. */
  onSave: (label: PromptLabel, applyModel: boolean) => Promise<void> | void;
}

const ChangeLabelDialog = ({
  open,
  onOpenChange,
  versionLabel,
  currentLabel,
  isSaving = false,
  versionModel,
  modelOptions,
  movesSubprojects,
  onSave,
}: ChangeLabelDialogProps): JSX.Element => {
  const initialSelected: PromptLabel =
    isPromptLabel(currentLabel) && ASSIGNABLE_LABELS.includes(currentLabel) ? currentLabel : DEFAULT_LABEL;
  const [selected, setSelected] = useState<PromptLabel>(initialSelected);
  const [error, setError] = useState<string | null>(null);
  const [applyModel, setApplyModel] = useState(false);
  const showModelChoice =
    !!modelOptions &&
    selected === 'production' &&
    currentLabel !== 'production' &&
    versionModel !== modelOptions.currentModel;

  // The active production version cannot be demoted directly — that would leave
  // the scope with no production prompt. To change it, promote another version.
  const isProductionRow = currentLabel === 'production';
  const isOptionDisabled = (label: PromptLabel): boolean => isProductionRow && label !== 'production';

  const canSave = selected !== currentLabel && !isOptionDisabled(selected) && !isSaving;

  const handleSave = async (): Promise<void> => {
    if (!canSave) return;
    setError(null);
    try {
      await onSave(selected, showModelChoice && applyModel);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))] max-w-none gap-0 overflow-hidden p-0 sm:max-w-none">
        <DialogHeader className="border-b border-border-subtle px-6 py-4">
          <DialogTitle className="text-base font-semibold">Etiquetas del prompt</DialogTitle>
          <DialogDescription className="text-xs text-text-muted">
            Las etiquetas identifican el estado de cada versión. La versión etiquetada como{' '}
            <span className="font-medium text-text-default">production</span> es la que utiliza el agente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 px-6 py-5">
          <div className="flex items-center justify-between gap-2 rounded-md border border-border-subtle bg-gray-50 px-3 py-2">
            <span className="text-xs text-text-muted">Versión {versionLabel}</span>
            {currentLabel ? (
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                  isPromptLabel(currentLabel) ? PROMPT_LABEL_META[currentLabel].chip : 'bg-gray-100 text-gray-500'
                }`}
              >
                {isPromptLabel(currentLabel) ? PROMPT_LABEL_META[currentLabel].text : currentLabel}
              </span>
            ) : (
              <span className="text-[11px] text-text-subtle">Sin etiqueta</span>
            )}
          </div>

          <RadioGroup value={selected} onValueChange={(value) => setSelected(value as PromptLabel)} className="gap-2">
            {ASSIGNABLE_LABELS.map((label) => {
              const meta = PROMPT_LABEL_META[label];
              const disabled = isOptionDisabled(label);
              return (
                <Label
                  key={label}
                  htmlFor={`change-label-${label}`}
                  className={cn(
                    'flex items-start gap-3 rounded-md border border-border-subtle px-3 py-2.5',
                    disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer hover:border-indigo-200 has-[[data-state=checked]]:border-indigo-300 has-[[data-state=checked]]:bg-indigo-50/50'
                  )}
                >
                  <RadioGroupItem value={label} id={`change-label-${label}`} disabled={disabled} className="mt-0.5" />
                  <div className="flex flex-1 flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Badge className={meta.chip}>{meta.text}</Badge>
                    </div>
                    <span className="text-[11px] text-text-muted">{meta.description}</span>
                  </div>
                </Label>
              );
            })}
          </RadioGroup>

          {showModelChoice && (
            <ModelChangeChoice
              currentModelName={getAgentModelName(modelOptions.currentModel, modelOptions.options)}
              versionModelName={getAgentModelName(versionModel, modelOptions.options)}
              applyModel={applyModel}
              onApplyModelChange={setApplyModel}
              movesSubprojects={movesSubprojects}
            />
          )}

          {isProductionRow && (
            <p className="text-[11px] text-text-muted">
              Para reemplazar la versión en producción, promueve otra versión a production.
            </p>
          )}
          {error && <p className="text-[11px] text-destructive">{error}</p>}
        </div>

        <DialogFooter className="m-0 gap-2 rounded-none border-t border-border-subtle bg-transparent px-6 py-4">
          <Button type="button" size="sm" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSave}
            onClick={handleSave}
            className="bg-gray-900 text-white hover:bg-gray-800"
          >
            {isSaving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ChangeLabelDialog;
